// Google Places address autocomplete — configuration hook + pure helpers.
//
// APPROVED PROVIDER: Google Places. This module is the ONLY address-suggestion
// path in the portal; the previous OpenStreetMap/Nominatim calls were removed so
// nothing can masquerade as Google.
//
// CONFIGURATION (owner action, not done here):
//   REACT_APP_GOOGLE_MAPS_API_KEY   — a BROWSER-RESTRICTED Google Maps/Places key
//                                     (HTTP-referrer restricted to the portal
//                                     domain, Places API + Maps JavaScript API
//                                     enabled).
// The key is a frontend/publishable value read from the build environment. No key
// is hardcoded here, none is invented, and no server credential is ever exposed:
// a referrer-restricted browser key is the only credential this path accepts.
//
// When the key is absent the portal degrades to explicit MANUAL ENTRY (clearly
// labeled) — order entry keeps working and required address/ZIP validation still
// applies. It never silently falls back to another provider.

const GOOGLE_KEY_ENV = "REACT_APP_GOOGLE_MAPS_API_KEY";

function readEnv(env) {
  const e = env || (typeof process !== "undefined" && process.env) || {};
  const v = e[GOOGLE_KEY_ENV];
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

// Whether Google Places is configured for this build.
function isGooglePlacesConfigured(env) {
  return readEnv(env) !== null;
}

// Safe diagnostic state for admin/dev (never exposes the key itself).
function googlePlacesStatus(env) {
  const key = readEnv(env);
  return key
    ? { configured: true, provider: "google", envVar: GOOGLE_KEY_ENV, keyPreview: `…${key.slice(-4)}` }
    : { configured: false, provider: "manual", envVar: GOOGLE_KEY_ENV,
        message: `Google Places is not configured. Set ${GOOGLE_KEY_ENV} to a browser-restricted Google Maps/Places key to enable address autocomplete. Manual address entry is active.` };
}

// Map Google `address_components` to the portal's address fields. Only values
// Google actually returned are used — nothing is guessed or back-filled.
function parseAddressComponents(components) {
  const get = (type, form) => {
    const c = (components || []).find((x) => (x.types || []).includes(type));
    return c ? (form === "short" ? c.short_name : c.long_name) : "";
  };
  const streetNumber = get("street_number");
  const route = get("route");
  const line1 = [streetNumber, route].filter(Boolean).join(" ");
  const city = get("locality") || get("sublocality") || get("postal_town") || "";
  const state = get("administrative_area_level_1", "short") || "";
  const zip = get("postal_code") || "";
  return { line1, city, state, zip };
}

// Build the standardized single-line address the portal stores.
function formatSelectedAddress(parts) {
  const p = parts || {};
  const cityState = [p.city, p.state].filter(Boolean).join(", ");
  return [p.line1, cityState, p.zip].filter(Boolean).join(", ");
}

// Normalize a Places Details result into { address, zip, city, state }.
function placeToAddress(place) {
  const parts = parseAddressComponents(place && place.address_components);
  return {
    address: formatSelectedAddress(parts) || (place && place.formatted_address) || "",
    zip: parts.zip || "",
    city: parts.city || "",
    state: parts.state || "",
  };
}

// Dynamically load the Google Maps JS API (Places library) once per page.
// Browser-only; returns a promise resolving to window.google, or rejecting when
// the key is missing/blocked so the caller can fall back to manual entry.
let loaderPromise = null;
function loadGooglePlaces(env) {
  if (typeof window === "undefined") return Promise.reject(new Error("no_window"));
  if (window.google && window.google.maps && window.google.maps.places) return Promise.resolve(window.google);
  if (loaderPromise) return loaderPromise;
  const key = readEnv(env);
  if (!key) return Promise.reject(new Error("google_places_not_configured"));
  loaderPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places&loading=async`;
    s.async = true;
    s.defer = true;
    s.onload = () => (window.google && window.google.maps && window.google.maps.places)
      ? resolve(window.google)
      : reject(new Error("google_places_unavailable"));
    s.onerror = () => { loaderPromise = null; reject(new Error("google_places_load_failed")); };
    document.head.appendChild(s);
  });
  return loaderPromise;
}

module.exports = {
  GOOGLE_KEY_ENV,
  isGooglePlacesConfigured, googlePlacesStatus,
  parseAddressComponents, formatSelectedAddress, placeToAddress,
  loadGooglePlaces,
};
